import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdCheck, MdKeyboardArrowDown } from 'react-icons/md';

const normalizeOptions = (options) => options.map((option) => (
  typeof option === 'string' ? { value: option, label: option } : option
));

const themeClasses = {
  light: {
    trigger: 'border-slate-200 bg-slate-50 text-slate-900 shadow-sm hover:border-slate-300 focus:border-[#9a51a0] focus:bg-white focus:ring-2 focus:ring-[#9a51a0]/20',
    placeholder: 'text-slate-500',
    icon: 'text-slate-500',
    menu: 'border-[#9a51a0]/20 bg-white shadow-[0_20px_45px_-20px_rgba(15,23,42,0.45)]',
    menuHeader: 'bg-gradient-to-r from-[#9a51a0] to-[#f36f21] text-white/90',
    option: 'text-slate-700 hover:bg-gradient-to-r hover:from-[#9a51a0]/8 hover:to-[#f36f21]/8',
    selectedOption: 'bg-gradient-to-r from-[#9a51a0]/12 to-[#f36f21]/12 text-slate-900',
    check: 'text-[#9a51a0]'
  },
  dark: {
    trigger: 'border-gray-600 bg-[#3c3c3c] text-white shadow-sm hover:bg-[#404040] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30',
    placeholder: 'text-gray-300',
    icon: 'text-gray-300',
    menu: 'border-gray-600 bg-[#2f2f2f] shadow-[0_20px_45px_-20px_rgba(0,0,0,0.65)]',
    menuHeader: 'bg-gradient-to-r from-[#1d4ed8] to-[#2563eb] text-white/95',
    option: 'text-gray-100 hover:bg-white/10',
    selectedOption: 'bg-blue-500/20 text-white',
    check: 'text-blue-300'
  }
};

function CustomSelect({
  id,
  name,
  value,
  options,
  placeholder,
  onChange,
  theme = 'light',
  buttonClassName = '',
  menuClassName = '',
  disabled = false
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedOption = normalizedOptions.find((option) => option.value === value);
  const classes = themeClasses[theme] || themeClasses.light;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSelect = (nextValue) => {
    onChange({
      target: {
        name,
        value: nextValue
      }
    });
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative min-w-0">
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setOpen((current) => !current)}
        disabled={disabled}
        className={`w-full min-w-0 rounded-2xl border px-3 py-2.5 pr-9 text-left text-sm transition-all sm:rounded-xl ${classes.trigger} ${buttonClassName} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`block truncate ${selectedOption ? '' : classes.placeholder}`}>
          {selectedOption?.label || placeholder}
        </span>
        <MdKeyboardArrowDown className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-transform ${classes.icon} ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !disabled && (
        <div className={`absolute left-0 right-0 top-[calc(100%+0.35rem)] z-30 overflow-hidden rounded-2xl border sm:rounded-xl ${classes.menu} ${menuClassName}`}>
          <div className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${classes.menuHeader}`}>
            {placeholder}
          </div>
          <div className="max-h-48 overflow-y-auto p-1.5">
            {normalizedOptions.map((option) => {
              const selected = option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`flex w-full min-w-0 items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm transition-all ${selected ? classes.selectedOption : classes.option}`}
                  role="option"
                  aria-selected={selected}
                >
                  <span className="min-w-0 truncate">{option.label}</span>
                  {selected && <MdCheck className={`h-4 w-4 shrink-0 ${classes.check}`} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomSelect;
